// Patient screens a kiosk hands off to that live outside the /kiosk tree: instant-action
// links, the check-in wizard, and the public invoice/receipt page. A kiosk check-in starts
// as /ia/<link> and then runs entirely on /checkin/*, and pushed payments land on
// /invoice/<token>, so without these the timer stops at the handoff.
const HANDOFF_PREFIXES = ['/ia/', '/instant-packet', '/checkin', '/invoice/'];

export const isHandoffScreen = (pathname) =>
  HANDOFF_PREFIXES.some((prefix) => pathname.startsWith(prefix));

// The check-in wizard is mounted at /checkin/*, /mobile/checkin/* and /kiosk/checkin/*, so a
// prefix match on any single mount misses the other two.
const isCheckinScreen = (pathname) => pathname.includes('/checkin');

// Reached from the kiosk footer and the instant-action sign-in, mid-flow.
export const isTermsScreen = (pathname) => pathname === '/terms' || pathname === '/privacy';

// Screens where the patient may still hold a signed-in session that has to be dropped.
export const endsPatientSession = (pathname) =>
  isHandoffScreen(pathname) || isCheckinScreen(pathname) || isTermsScreen(pathname);

// Filling out a check-in takes real reading and typing time, so it gets a far longer
// budget than the kiosk's own short landing screens.
const CHECKIN_IDLE_SECONDS = 180;
// A card-present sale pauses the timer outright (see SALE_PAUSE_MAX_SECONDS), so this budget
// only covers a finished/failed/canceled screen left sitting.
const PAYMENT_IDLE_SECONDS = 240;
const KIOSK_IDLE_SECONDS = 60;
const MOBILE_IDLE_SECONDS = 600;
export const WARNING_SECONDS = 30;
// Mirrors the kiosk app's sale-window ping pause (salePingPauseMaxSeconds): past it the reader
// has long since timed the sale out, so a sale that never reported back can't pin the screen.
export const SALE_PAUSE_MAX_SECONDS = 180;

export const resolveIdleSeconds = (pathname) => {
  // Saving a card is the same reader-driven flow as a sale, so it gets the same budget.
  if (pathname.startsWith('/kiosk/payment') || pathname.startsWith('/kiosk/save-card'))
    return PAYMENT_IDLE_SECONDS;
  if (pathname.startsWith('/mobile/')) return MOBILE_IDLE_SECONDS;
  if (isCheckinScreen(pathname)) return CHECKIN_IDLE_SECONDS;
  return KIOSK_IDLE_SECONDS;
};

// Where to send the patient back to when a shared/kiosk device goes idle.
export const resolveIdleReset = (pathname) => {
  if (isTermsScreen(pathname)) {
    const cameFrom = localStorage.getItem('myriad-kiosk-prev-url') || '';
    return cameFrom.startsWith('/mobile/') ? '/mobile/idle' : '/kiosk/idle';
  }
  if (pathname.startsWith('/mobile/')) return '/mobile/idle';
  if (isHandoffScreen(pathname)) return '/kiosk/idle';
  return '/kiosk';
};

const pluralizeSeconds = (seconds) => `${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;

export const resolveWarningMessage = ({ pathname, remaining, clearsInPlace }) => {
  const countdown = pluralizeSeconds(remaining);
  if (clearsInPlace) return `For your privacy, we'll clear this form in ${countdown}.`;
  if (endsPatientSession(pathname)) return `For your privacy, we'll sign you out in ${countdown}.`;
  return `For your privacy, this screen will reset in ${countdown}.`;
};
